import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { supabase } from '../utils/supabaseClient';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    // Loading for auth transitions (sign-in/out). Should not block initial route rendering.
    const [authLoading, setAuthLoading] = useState(false);
    // Marks that we've computed initial auth state (session) at least once
    const [authReady, setAuthReady] = useState(false);
    // Admin role loading is tracked separately to avoid blocking non-admin routes
    const [adminLoaded, setAdminLoaded] = useState(false);

    // Memoized function to fetch admin status
    const fetchAdminStatus = useCallback(async (userId) => {
        if (!userId) {
            setIsAdmin(false);
            localStorage.removeItem('is_admin');
            setAdminLoaded(true);
            return false;
        }

        try {
            const { data: profile, error: profileError } = await supabase
                .from('profiles')
                .select('is_admin')
                .eq('id', userId)
                .single();

            if (profileError) {
                console.error('Error fetching user profile:', profileError.message);
                setIsAdmin(false);
                localStorage.removeItem('is_admin');
                setAdminLoaded(true);
                return false;
            }

            const adminStatus = Boolean(profile?.is_admin);
            setIsAdmin(adminStatus);
            localStorage.setItem('is_admin', JSON.stringify(adminStatus));
            setAdminLoaded(true);
            return adminStatus;
        } catch (error) {
            console.error('Error in fetchAdminStatus:', error);
            setIsAdmin(false);
            localStorage.removeItem('is_admin');
            setAdminLoaded(true);
            return false;
        }
    }, []);

    // Function to handle user session
    const handleUserSession = useCallback(async (session) => {
        if (session?.user) {
            setUser(session.user);
            // Optimistic admin from cache for immediate UI
            const cachedAdminStatus = localStorage.getItem('is_admin');
            if (cachedAdminStatus !== null) {
                try {
                    const parsedStatus = JSON.parse(cachedAdminStatus);
                    setIsAdmin(Boolean(parsedStatus));
                } catch (e) {
                    console.error('Error parsing cached admin status:', e);
                    setIsAdmin(false);
                }
            }
            setAdminLoaded(false);
            // Refresh admin in background (do not block auth readiness)
            fetchAdminStatus(session.user.id);
        } else {
            setUser(null);
            setIsAdmin(false);
            localStorage.removeItem('is_admin');
            setAdminLoaded(true);
        }
    }, [fetchAdminStatus]);

    useEffect(() => {
        let mounted = true;
        let authListener = null;

        const initializeAuth = async () => {
            try {
                const { data: { session }, error } = await supabase.auth.getSession();
                if (error) {
                    console.error('Error getting session:', error.message);
                }
                if (!mounted) return;
                // Set initial session and mark auth ready quickly
                await handleUserSession(session);
                setAuthReady(true);

                // Subscribe to auth changes
                const { data: { subscription } } = supabase.auth.onAuthStateChange(
                    async (event, session) => {
                        if (!mounted) return;
                        // Mark loading only for explicit sign-in/out transitions
                        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
                            setAuthLoading(true);
                        }
                        await handleUserSession(session);
                        setAuthLoading(false);
                    }
                );
                authListener = subscription;
            } catch (error) {
                console.error('Error initializing auth:', error);
                if (mounted) {
                    setUser(null);
                    setIsAdmin(false);
                    setAdminLoaded(true);
                    setAuthReady(true);
                    setAuthLoading(false);
                }
            }
        };

        initializeAuth();

        return () => {
            mounted = false;
            if (authListener) authListener.unsubscribe();
        };
    }, [handleUserSession]);

    const loginWithGoogle = async () => {
        try {
            setAuthLoading(true);
            
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    // Return to home after login (no auto-redirect to /quiz-selection)
                    redirectTo: `${window.location.origin}/`
                }
            });

            if (error) {
                console.error('Error during Google login:', error.message);
                setAuthLoading(false);
                return { error };
            }

            return { error: null };
        } catch (error) {
            console.error('Error in loginWithGoogle:', error);
            setAuthLoading(false);
            return { error };
        }
    };

    const logout = async () => {
        try {
            setAuthLoading(true);
            
            const { error } = await supabase.auth.signOut();
            
            if (error) {
                console.error('Error during logout:', error.message);
            }
            
            // Clear state immediately
            setUser(null);
            setIsAdmin(false);
            localStorage.removeItem('is_admin');
            setAuthLoading(false);
            
            return { error };
        } catch (error) {
            console.error('Error in logout:', error);
            setAuthLoading(false);
            return { error };
        }
    };

    const value = {
        user,
        isAdmin,
        adminLoaded,
        authLoading,
        authReady,
        loginWithGoogle,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};